/**
 * Per-page SEO metadata.
 *
 * Until this existed every URL on the site shipped the same
 * <title>Property People</title> and the same meta description, so search
 * engines had nothing to tell ~330 pages apart. These builders derive a
 * unique, keyword-bearing title and a description from each record's own
 * copy.
 *
 * Pure functions on purpose - no React in here, so the title formulas can be
 * reasoned about (and tested) independently of the components that use them.
 */

const { PRODUCT_ORIGIN, BRANDS, brandForDistrict } = require("./brand");
const { propertyPath } = require("./propertySlug");
const { postPath } = require("./postSlug");
const { extractYouTubeId, youtubeEmbedUrl } = require("./youtubeVideo");

const SITE_NAME = "Property People";

// Must match the canonical hostname used by api/scripts/generate-sitemap.js
// and the Sitemap: line in robots.txt. If the www/apex 301 is ever settled
// the other way, all three change together.
const ORIGIN = "https://property-people.co.uk";

const DEFAULT_DESCRIPTION =
    "Independent estate and letting agents covering Southfields, Putney, Wimbledon, Earlsfield and South West London.";

// Fallback share image: a real photograph of the Tooting High Street office,
// already in the build at 1872x746 - comfortably over the 1200x630 that
// Facebook and WhatsApp want.
const DEFAULT_OG_IMAGE = ORIGIN + "/images/pp-outside-dark.jpg";

// The account linked from the site footer.
const TWITTER_HANDLE = "@Property_People";

/** Strip tags and collapse whitespace so stored HTML is safe in a meta tag. */
function stripHtml(html) {
    if (!html) return "";
    return String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, " ")
        .trim();
}

/** Trim to `max` characters on a word boundary. */
function truncate(text, max) {
    const limit = max || 155;
    const clean = String(text || "").trim();
    if (clean.length <= limit) return clean;

    const cut = clean.slice(0, limit);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[,.;:\-\s]+$/, "") + "…";
}

/** 1400 + "PCM" -> "£1,400 PCM"   650000 + "" -> "£650,000" */
function formatPrice(price, priceType) {
    if (price === undefined || price === null || price === "") return "";
    const withCommas = String(price).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return "£" + withCommas + (priceType ? " " + priceType : "");
}

/**
 * "to Rent" / "for Sale" while a property is live, past tense once it is not.
 * `status` is one of Available | Under Offer | Let | Sold.
 */
function disposition(category, status) {
    if (status === "Let") return "Let";
    if (status === "Sold") return "Sold";
    return category === "Sales" ? "for Sale" : "to Rent";
}

/**
 * Title and description for one listing.
 *
 * The price is load-bearing, not decoration. Measured against all 281
 * published listings: beds + type + location alone collides on 136 pages,
 * adding displayAddress still collides on 31, and adding the price brings it
 * down to a single pair - two genuinely near-identical 3-bed flats at March
 * Court, Putney. So the price stays in every title, including Let and Sold
 * ones, which are 243 of the 281.
 */
function propertySeo(property) {
    if (!property || !property._id) return null;

    const beds = property.bedrooms ? property.bedrooms + " Bed " : "";
    const type = property.type || "Property";
    const where = [property.displayAddress, property.location].filter(Boolean).join(", ");
    const disp = disposition(property.category, property.status);

    // priceType is PCM / PW, which only means anything for a rental. At least
    // one sales record carries "PCM", which would otherwise render the
    // nonsense "£700,000 PCM".
    const isLetting = property.category !== "Sales";
    const price = formatPrice(property.price, isLetting ? property.priceType : "");

    let headline;
    if (disp === "Let" || disp === "Sold") {
        // Past tense reads honestly, and keeps these out of "to rent" queries
        // they can no longer satisfy.
        headline = beds + type + (where ? ", " + where : "") + " - " + disp;
        if (price) headline += ", " + price;
    } else {
        headline = beds + type + " " + disp + (where ? ", " + where : "");
        if (price) headline += " - " + price;
    }

    // A few displayAddress values contain a whole sentence rather than an
    // address ("5 bed terraced house for sale Aldis Street, London SW17"),
    // which produced a 124-character title. Cap the headline so no title runs
    // away; the discriminating words are already at the front.
    if (headline.length > 90) {
        headline = truncate(headline, 90);
    }

    // The listing's own marketing copy is the most relevant description
    // available; fall back to the long description, then to a built sentence.
    const ownWords = stripHtml(property.summary) || stripHtml(property.description);
    const description = ownWords
        ? truncate(ownWords, 155)
        : truncate(
              beds + type + " " + disp.toLowerCase() + (where ? " in " + where : "") + (price ? " at " + price : "") + ".",
              155
          );

    return {
        title: headline + " | " + SITE_NAME,
        description: description,
        canonical: ORIGIN + propertyPath(property),
        // The listing's own first photo is by far the most compelling share
        // image; fall back to the office shot when a listing has none.
        image: property.photos && property.photos.length ? property.photos[0] : DEFAULT_OG_IMAGE,
        imageAlt: headline,
        type: "website",
    };
}

/** Title and description for one blog post. All 19 posts have an excerpt. */
function postSeo(post) {
    if (!post || !post._id) return null;

    const title = post.title ? post.title + " | " + SITE_NAME : SITE_NAME;
    const body = stripHtml(post.excerpt) || stripHtml(post.content);

    // Standard 16:9 - what YouTube's own embed iframe uses regardless of the
    // source video's actual resolution, and what Facebook/X expect here.
    const videoId = post.videoUrl ? extractYouTubeId(post.videoUrl) : null;
    const video = videoId ? { url: youtubeEmbedUrl(videoId), width: 1280, height: 720 } : undefined;

    return {
        title: title,
        description: body ? truncate(body, 155) : DEFAULT_DESCRIPTION,
        canonical: ORIGIN + postPath(post),
        // Posts can now carry their own featured image, set in the admin
        // form. Those that do not fall back to the office photograph, which
        // is a truthful picture of the business even if it illustrates
        // nothing in particular about the article.
        image: post.image || DEFAULT_OG_IMAGE,
        imageAlt: post.title || SITE_NAME,
        type: "article",
        publishedTime: post.createdAt ? new Date(post.createdAt).toISOString() : undefined,
        modifiedTime: post.updatedAt ? new Date(post.updatedAt).toISOString() : undefined,
        video: video,
    };
}

/**
 * A plain-English description of a property, reused as the basis for image
 * alt text: "1 bedroom Flat at Vantage House, Weir Road, Wimbledon".
 */
function propertyAltBase(property) {
    if (!property) return "";
    const beds = property.bedrooms ? property.bedrooms + " bedroom " : "";
    const type = property.type || "property";
    const where = [property.displayAddress, property.location].filter(Boolean).join(", ");
    return (beds + type + (where ? " at " + where : "")).trim();
}

/**
 * Alt text for one photo in a set.
 *
 * Note what this deliberately does NOT do: claim what the photo shows. There
 * is no way to know from the record whether photo 3 is a kitchen or a garden,
 * and "Open-plan kitchen" on a picture of a bathroom is worse for a screen
 * reader than saying nothing. Describing the property and the photo's
 * position is accurate for every image. Genuinely per-photo descriptions
 * would need captions entered alongside the uploads.
 */
function photoAlt(altBase, index, total) {
    const base = altBase || "Property photo";
    if (typeof index === "number" && total > 1) {
        return "Photo " + (index + 1) + " of " + total + ": " + base;
    }
    return base;
}

/**
 * Title and description for an area landing page.
 *
 * The description is built from counts that come out of the database, so it
 * differs per area and states nothing that is not true of the actual stock.
 * `lettingsLed` decides the wording because 264 of 309 listings are lettings -
 * "letting agents in X" is the term that matters commercially.
 */
function localitySeo(area, stats) {
    if (!area) return null;

    const s = stats || {};
    const lettingsLed = s.lettingsLed !== false;
    const service = lettingsLed ? "Letting Agents" : "Estate Agents";

    let description;
    if (s.total) {
        const bedRange =
            s.minBeds !== null && s.maxBeds !== null && s.maxBeds > s.minBeds
                ? " from " + (s.minBeds === 0 ? "studios" : s.minBeds + " bed") + " to " + s.maxBeds + " bed"
                : "";
        description =
            "Property People have let or sold " +
            s.total +
            " properties in " +
            area.name +
            bedRange +
            "." +
            (s.live ? " " + s.live + " available now." : "") +
            " Local agents since 2010.";
    } else {
        description =
            "Property People cover " +
            area.name +
            " for lettings, sales and property management. Local independent agents since 2010.";
    }

    return {
        title: service + " in " + area.name + " | " + SITE_NAME,
        description: truncate(description, 155),
        canonical: ORIGIN + "/estate-agents/" + area.slug,
        image: DEFAULT_OG_IMAGE,
        imageAlt: service + " in " + area.name,
        type: "website",
    };
}

/**
 * A house price district page. The description carries the two numbers
 * somebody searching "house prices SW17" is looking for - the median and how
 * many sales it rests on - because a search result that answers the question
 * in the snippet is the one that gets the click, and because a description
 * built from the data cannot go stale against the page the way a written one
 * would.
 *
 * `stats` may be null: the renderer asks for the head tags before it knows
 * whether the district has any sales, and a page that exists with no figures
 * still needs a title.
 */
/**
 * The index of district pages - one per brand, describing different things.
 *
 * The agency's covers the 81 districts of its own patch; the product's covers
 * the ~1,853 everywhere else. Sharing one entry in STATIC_SEO put the
 * product's name and the agency's description on both, which is how the agency
 * index came to be titled "| Doorstep Data" while promising "south west London
 * and Surrey" to a national audience.
 *
 * THE COUNT IS PASSED IN, not written down. Both numbers move whenever the
 * sales floor is reapplied, and a description promising 81 districts to a page
 * listing 79 is the kind of wrong nobody notices for a year.
 *
 * @param {object} brand    from brandForHost / brandForDistrict
 * @param {number} count    how many districts this brand's index actually lists
 */
function housePricesIndexSeo(brand, count) {
    const product = brand.key === "product";
    const where = product ? "England and Wales" : "South West London and Surrey";
    const n = typeof count === "number" && count > 0 ? count.toLocaleString("en-GB") : null;

    return {
        title: product
            ? "House Prices by Postcode District - Sold Prices | " + brand.name
            : "House Prices in South West London by Postcode District | " + brand.name,
        siteName: brand.name,
        description:
            "Median sold prices, ten-year trends and recent sales for " +
            (n ? n + " postcode districts" : "postcode districts") +
            " across " + where + ", from HM Land Registry data.",
        canonical: brand.origin + "/house-prices",
    };
}

function housePricesSeo(district, stats) {
    if (!district) return null;
    const s = stats || {};
    const where = s.authority ? district + ", " + s.authority : district;

    const description = s.median
        ? "The median home in " +
          district +
          " sold for " +
          money(s.median) +
          " over the last " +
          s.years +
          " years, across " +
          s.total.toLocaleString("en-GB") +
          " sales. Prices by property type and year, from HM Land Registry."
        : "Sold prices for " + district + " from HM Land Registry Price Paid data.";

    /*
     * WHICH BRAND'S PAGE THIS IS depends on the district, not on the path.
     * The five South West London areas are the agency's; the other 99 are the
     * product's. See the note in brand.js for why.
     *
     * Name and canonical have to come from the SAME decision. A title ending
     * "| Doorstep Data" above a canonical pointing at property-people.co.uk
     * tells a search engine two different things about one page, and it is
     * exactly the sort of mismatch that is invisible until something is
     * ranking badly for no apparent reason.
     */
    const owner = brandForDistrict(district);

    return {
        // This is the line that shows in a search result, so getting the
        // brand wrong here undoes the split in the one place everybody sees.
        title: "House Prices in " + where + " - Sold Prices & Median | " + owner.name,
        siteName: owner.name,
        description: truncate(description, 155),
        canonical: owner.origin + "/house-prices/" + district.toLowerCase(),
        image: DEFAULT_OG_IMAGE,
        imageAlt: "House prices in " + district,
        type: "website",
    };
}

/** Whole pounds, the way a price is written rather than the way it is stored. */
function money(n) {
    return "£" + Math.round(Number(n)).toLocaleString("en-GB");
}

/**
 * The product's version of a shared page.
 *
 * The report tool is the same tool on both domains and is one component, so
 * the only thing that differs is what the head says about it: a different
 * name, a different canonical, and copy that does not describe an estate
 * agency. Keyed by route like STATIC_SEO, and looked up instead of it when
 * the hostname is the product's.
 *
 * Separate object rather than a second entry in STATIC_SEO because that one
 * is keyed by route and the route is the same on both - there is nowhere to
 * put a second value.
 */
/*
 * The account pages, all of them noindex.
 *
 * NOTHING HERE IS CONTENT. A sign-in page in search results is a phishing
 * target and a support call, a verify or reset page is meaningless without
 * the token that is not in the URL a crawler sees, and none of it should
 * compete for attention with the pages that are trying to rank.
 *
 * They still carry a title and a canonical: a browser tab and a shared link
 * should say something sensible even for a page search engines are told to
 * ignore.
 */
const ACCOUNT_PAGES = {
    "/account": ["Your account", "Your Doorstep Data account."],
    "/account/sign-in": ["Sign in", "Sign in to Doorstep Data."],
    "/account/register": ["Create an account", "Create a Doorstep Data account."],
    "/account/verify": ["Confirm your email", "Confirming your email address."],
    "/account/forgot": ["Forgotten password", "Send yourself a password reset link."],
    "/account/reset": ["Set a new password", "Choose a new password."],
};

const PRODUCT_SEO = {
    "/": {
        title: BRANDS.product.name + " - Free Property Data for Any UK Address",
        description:
            "Council tax, energy performance, sold prices, running costs, flood risk and local data for any address in England and Wales. Free, no account, from government sources.",
        canonical: PRODUCT_ORIGIN + "/",
        siteName: BRANDS.product.name,
    },
    "/property-report": {
        title: "Free Property Report by Postcode - Council Tax, EPC, Sold Prices | " + BRANDS.product.name,
        description:
            "Council tax, energy performance, sold prices, running costs, flood risk and local data for any address in England and Wales. Free, from official government sources.",
        canonical: PRODUCT_ORIGIN + "/property-report",
        siteName: BRANDS.product.name,
    },
    "/privacy-policy": {
        title: "Privacy Policy | " + BRANDS.product.name,
        description:
            "No account, no cookies, no trackers, nothing loaded from anyone else. What Doorstep Data keeps is a 14-day server log, and this says so plainly.",
        canonical: PRODUCT_ORIGIN + "/privacy-policy",
        siteName: BRANDS.product.name,
    },
    "/contact": {
        title: "Contact | " + BRANDS.product.name,
        description:
            "Doorstep Data is a trading name of Property People London Ltd (No. 07355433), 204 Tooting High Street, London SW17 0SG. How to reach us, and what to do if a figure looks wrong.",
        canonical: PRODUCT_ORIGIN + "/contact",
        siteName: BRANDS.product.name,
    },
};

/*
 * The account pages, folded in after the fact rather than written out.
 *
 * ALL OF THEM noindex. None of it is content: a sign-in page in search
 * results is a phishing target and a support call, and a verify or reset
 * page is meaningless without the token a crawler never sees. They keep a
 * title and a canonical anyway, because a browser tab and a shared link
 * should still say something sensible.
 *
 * A LOOP AND NOT A SPREAD. Written first as an object spread into the
 * literal above, which webpack 4 would not take - the build failed with
 * "DEFAULT_DESCRIPTION is not exported from ../data/seo", because the
 * spread defeated its static analysis of this CommonJS module and it then
 * could not see ANY of the exports. Node loaded the same file happily,
 * which is what made the error so misleading. This file is required by
 * plain Node as well as bundled (section 6d), so it has to satisfy both.
 */
Object.keys(ACCOUNT_PAGES).forEach(function (path) {
    PRODUCT_SEO[path] = {
        title: ACCOUNT_PAGES[path][0] + " | " + BRANDS.product.name,
        description: ACCOUNT_PAGES[path][1],
        canonical: PRODUCT_ORIGIN + path,
        siteName: BRANDS.product.name,
        noindex: true,
    };
});

/**
 * Static routes. Copy is written from what the site actually claims - the
 * office really is on Tooting High Street and the navbar really does say
 * Established 2010. Nothing here asserts anything the business does not.
 */
const STATIC_SEO = {
    "/": {
        title: "Estate & Letting Agents in Southfields, Putney & Wimbledon | " + SITE_NAME,
        description:
            "Independent estate and letting agents for South West London. Sales, lettings and property management across Southfields, Putney, Wimbledon and Earlsfield since 2010.",
    },
    "/about-us": {
        title: "About Us - Independent South West London Agents | " + SITE_NAME,
        description:
            "Property People has been letting and selling homes across South West London since 2010. Local knowledge, straight answers and a team based on Tooting High Street.",
    },
    "/pricelist": {
        title: "Landlord & Vendor Fees - Price List | " + SITE_NAME,
        description:
            "Itemised fees for landlords and vendors across our lettings, management and sales services in South West London. Clear pricing, no surprises.",
    },
    "/blog": {
        title: "Property Advice & South West London Area Guides | " + SITE_NAME,
        description:
            "Advice for landlords, tenants, buyers and sellers, plus local area guides to Southfields, Putney, Clapham and Earlsfield from our team.",
    },
    "/testimonials": {
        title: "Client Reviews & Testimonials | " + SITE_NAME,
        description:
            "What landlords, tenants, buyers and sellers say about working with Property People across South West London.",
    },
    "/meet-the-team": {
        title: "Meet the Team - Our South West London Agents | " + SITE_NAME,
        description:
            "The people behind Property People. Meet the sales, lettings and management team covering Southfields, Putney, Wimbledon and the surrounding areas.",
    },
    "/contact": {
        title: "Contact Us - 204 Tooting High Street | " + SITE_NAME,
        description:
            "Get in touch with Property People at 204 Tooting High Street, London SW17 0SG. Call 020 8946 7171 for sales, lettings, management or a valuation.",
    },
    "/property-report": {
        title: "Free Property Report - Council Tax, EPC & Local Data | " + SITE_NAME,
        description:
            "Enter a postcode for council tax, energy performance, sold prices and broadband for homes across England and Wales. Free, from official government sources.",
    },
    /*
     * Kept so anything looking up STATIC_SEO["/house-prices"] still finds an
     * entry, but the live pages do NOT use it - both brands have an index now
     * and they describe different things. housePricesIndexSeo() below is what
     * the renderer and the component call.
     */
    "/house-prices": {
        title: "House Prices by Postcode District - Sold Prices | " + BRANDS.product.name,
        siteName: BRANDS.product.name,
        description:
            "Median sold prices, ten-year trends and recent sales for postcode districts across England and Wales, from HM Land Registry data.",
    },
    "/privacy-policy": {
        title: "Privacy Policy | " + SITE_NAME,
        description: "How Property People London Ltd collects, uses and protects your personal data.",
    },
    "/sales-guide": {
        title: "Sales Guide for Sellers (2026) | " + SITE_NAME,
        description:
            "The 2026 sales guide for selling your South West London home. No sale no fee from 1.2% incl. VAT, free valuation, EPC and stamp duty explained, and the full selling process.",
        image: ORIGIN + "/images/guides/sales-hero.jpg",
    },
    "/lettings-guide": {
        title: "Lettings Guide for Landlords (2026) | " + SITE_NAME,
        description:
            "The 2026 lettings guide for landlords in South West London. Renters' Rights Act explained, landlord legal obligations, letting services and fees from 8.4% incl. VAT, no upfront costs.",
        image: ORIGIN + "/images/guides/letting-hero.jpg",
    },
    "/management-guide": {
        title: "360 Management Guide (2026) | " + SITE_NAME,
        description:
            "The 2026 guide to 360 Management for landlords in South West London. Fully managed renting for one all-inclusive 15% fee incl. VAT — marketing, compliance, inspections, legal notices and a free handyman service.",
        image: ORIGIN + "/images/guides/letting-hero.jpg",
    },
    "/client-money-protection": {
        title: "Client Money Protection | " + SITE_NAME,
        description:
            "Property People is a Propertymark Client Money Protection scheme member. See our current CMP certificate and what it means for landlords and tenants.",
        image: ORIGIN + "/images/legal/cmp-certificate.jpg",
    },
    "/ico-registration": {
        title: "Data Protection Registration (ICO) | " + SITE_NAME,
        description:
            "Property People London Ltd is registered with the Information Commissioner's Office, reference ZA005338. See and download our data protection registration certificate.",
        image: ORIGIN + "/images/legal/ico-certificate.jpg",
    },
};

/*
 * CommonJS on purpose: api/render.js requires this directly so the HTML the
 * server sends and the tags React sets in the browser come from one
 * implementation. Two copies would drift, and a page whose served title
 * differs from its rendered title sends a search engine mixed signals.
 * webpack resolves the named imports elsewhere in the app unchanged.
 */
module.exports = {
    SITE_NAME,
    ORIGIN,
    DEFAULT_DESCRIPTION,
    DEFAULT_OG_IMAGE,
    TWITTER_HANDLE,
    stripHtml,
    truncate,
    formatPrice,
    propertySeo,
    postSeo,
    propertyAltBase,
    photoAlt,
    localitySeo,
    housePricesIndexSeo,
    housePricesSeo,
    STATIC_SEO,
    PRODUCT_SEO,
};
